import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { Avatar } from 'src/game/components/shared/Avatar';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { RacesScore } from './RacesScore';
import { RaceType } from 'src/replicant/match2/races/races.ruleset';
import { TimedRaceThemeConfig } from '../races.utils';
import { user } from 'src/StateObserver';
import { getRaceRewardsConfig } from 'src/replicant/match2/races/races.getters';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { Match2RewardItem } from '../../components/Match2RewardItem';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { getPlayerProfileAvatarFrame } from 'src/replicant/profile/profile.getters';

export type RacesEntryParams = {
  name: string;
  position: number;
  score: number;
  avatar: string;
  me?: boolean;
  alt?: boolean;
};

export class RacesEntry extends View {
  private readonly bg: ImageScaleView;
  private readonly positionText: LangBitmapFontTextView;
  private readonly positionIcon: ImageScaleView;
  private readonly avatar: Avatar;
  private readonly name: LangBitmapFontTextView;
  private readonly score: RacesScore;
  private readonly prizes: FlexBoxView;
  private readonly left: View;
  private readonly right: View;

  constructor(
    opts: ViewOpts,
    private readonly raceType: RaceType,
    private readonly theme: TimedRaceThemeConfig,
  ) {
    super({ width: 570, height: 75, ...opts });

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height + 7,
      image: 'assets/ui/match2/common/list_entry.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 22, right: 22 },
        vertical: { top: 22, bottom: 22 },
      },
    });

    this.left = new View({
      superview: this,
    });

    this.right = new View({
      superview: this,
    });

    this.name = new LangBitmapFontTextView({
      superview: this.left,
      x: 140,
      y: 0,
      align: 'left',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      width: 150,
      height: this.style.height,
      color: '#754111',
    });

    this.positionText = new LangBitmapFontTextView({
      superview: this.left,
      x: 40,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      font: 'Title',
      width: this.style.height,
      height: this.style.height,
    });

    this.positionIcon = new ImageScaleView({
      superview: this.left,
      x: 40,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      align: 'center',
      verticalAlign: 'center',
      width: 62,
      height: 62,
    });

    this.avatar = new Avatar({
      superview: this.left,
      width: 58,
      height: 58,
      x: 105,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
    });

    this.score = new RacesScore({
      superview: this.right,
      x: this.style.width - 130,
      y: 0,
      width: 80,
      height: this.style.height,
    });

    this.prizes = new FlexBoxView({
      superview: this.right,
      x: this.style.width - 270,
      y: 10,
      width: 130,
      height: 60,
      flex: {
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        alignContent: 'center',
      },
    });
  }

  public setup(params: RacesEntryParams) {
    this.name.localeText = () => params.name;
    this.score.setup({ score: params.score, icon: this.theme.icon });

    if (params.position >= 1 && params.position <= 3) {
      this.positionIcon.updateOpts({
        image: `assets/ui/match2/tournament/star_tournament_medal_${params.position}.png`,
        visible: true,
      });
      this.positionText.updateOpts({ visible: false });
    } else {
      this.positionText.updateOpts({ visible: true });
      this.positionIcon.updateOpts({ visible: false });
      this.positionText.localeText = () => params.position.toString();
    }

    this.avatar.updateImage(params.avatar);
    this.avatar.setFrame(
      params.me || params.alt ? getPlayerProfileAvatarFrame(user()) : 'default',
    );

    const rewardsConfig = getRaceRewardsConfig();
    const prizes = rewardsConfig[this.raceType][params.position - 1] ?? [];
    this.setPrizes(prizes);

    if (params.alt) {
      this.left.updateOpts({ offsetX: 10 });
      this.right.updateOpts({ offsetX: -10 });
      this.bg.updateOpts({
        offsetY: -7,
        image: 'assets/ui/match2/common/list_entry_alt_red.png',
      });
    } else {
      this.left.updateOpts({ offsetX: 0 });
      this.right.updateOpts({ offsetX: 0 });
      this.bg.updateOpts({
        offsetY: 0,
        image: 'assets/ui/match2/common/list_entry.png',
      });
    }

    if (params.me) {
      this.bg.updateOpts({
        image: 'assets/ui/match2/common/list_entry_red.png',
      });
    } else {
      this.bg.updateOpts({
        opacity: 1,
      });
    }
  }

  public setPrizes(prizes: Match2Reward[]) {
    this.prizes.removeAllSubviews();
    prizes.forEach((prize) => {
      const prizeView = new Match2RewardItem({
        superview: this.prizes,
        size: 55,
        labelOffsetY: 5,
        labelSize: 20,
      });
      prizeView.setReward(prize);
    });
  }
}
